import React, { createContext, useContext, useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { supabase, checkSubscription } from './supabaseClient'
import type { Session } from '@supabase/supabase-js'
import LoginPage from './pages/LoginPage'
import Dashboard from './pages/Dashboard'
import Editor from './pages/Editor'
import ExampleChart from './pages/ExampleChart'
import BookExample from './pages/BookExample'

export const SubscriptionContext = createContext<boolean>(false)
export const useSubscription = () => useContext(SubscriptionContext)

// ── Error boundary — catches crashes so we see the message instead of blank ──

interface EBState { error: Error | null }

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, EBState> {
  state: EBState = { error: null }

  static getDerivedStateFromError(error: Error): EBState {
    return { error }
  }

  render() {
    const { error } = this.state
    if (error) {
      return (
        <div style={{ padding: '40px', fontFamily: 'sans-serif', maxWidth: '700px', margin: '60px auto' }}>
          <h2 style={{ color: '#dc2626', marginBottom: '12px' }}>Something went wrong loading the editor</h2>
          <pre style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '20px', fontSize: '13px', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {error.message}
            {'\n\n'}
            {error.stack}
          </pre>
          <button
            onClick={() => window.location.href = '/'}
            style={{ marginTop: '20px', padding: '10px 20px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
          >
            ← Back to Dashboard
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isSubscriber, setIsSubscriber] = useState(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
      if (session) checkSubscription().then(setIsSubscriber)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      if (session) checkSubscription().then(setIsSubscriber)
      else setIsSubscriber(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'sans-serif' }}>
        <p style={{ color: '#666' }}>Loading...</p>
      </div>
    )
  }

  return (
    <SubscriptionContext.Provider value={isSubscriber}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!session ? <LoginPage /> : <Navigate to="/" replace />} />
          <Route path="/" element={session ? <Dashboard /> : <Navigate to="/login" replace />} />
          <Route path="/chart/:id" element={session ? <ErrorBoundary><Editor /></ErrorBoundary> : <Navigate to="/login" replace />} />
          <Route path="/example" element={session ? <ExampleChart /> : <Navigate to="/login" replace />} />
          <Route path="/book-example" element={session ? <BookExample /> : <Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </SubscriptionContext.Provider>
  )
}
